import React, { useEffect, useState } from 'react';
import { can } from '../../utils/permissions';
import { fetchAPIPromise, getSubdomain, postAPI } from '../../utils/utils';
import {
    formatStripeDeadline,
    hasBusinessVerificationDocumentRequirement,
} from '../../utils/stripeRequirements';
import './StripeVerificationBanner.css';

const STRIPE_VERIFICATION_COLLES = {
    esquerdats: {
        displayName: 'Esquerdats',
        accountId: 'acct_1SpB4qCzFFqV7SLP',
        fallbackDeadline: '19 de maig de 2026',
    },
    ganapies: {
        displayName: 'Ganàpies',
        accountId: 'acct_1Szw5GCgWNiV1Bxc',
        fallbackDeadline: '31 de maig de 2026',
    },
};

function canManageStripeVerification(userInfo) {
    if (!userInfo || Object.keys(userInfo).length === 0) {
        return false;
    }

    return can(userInfo, 'MANAGE_QUOTES') || parseInt(userInfo.es_junta || 0, 10) >= 1;
}

function StripeVerificationBanner({ isLogged, userInfo }) {
    const colla = getSubdomain();
    const collaConfig = STRIPE_VERIFICATION_COLLES[colla];
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState(null);

    const canManage = canManageStripeVerification(userInfo);

    useEffect(() => {
        if (!collaConfig || isLogged !== true || !canManage) {
            return undefined;
        }

        let cancelled = false;
        setLoading(true);
        setError(null);

        fetchAPIPromise('/stripe/connect/status', false)
            .then(data => {
                if (!cancelled && data?.success) {
                    setStatus(data);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setStatus(null);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [collaConfig, isLogged, canManage]);

    if (!collaConfig || isLogged !== true || !canManage || loading) {
        return null;
    }

    const isTargetAccount = !collaConfig.accountId || status?.accountId === collaConfig.accountId;
    const needsBusinessDocument = hasBusinessVerificationDocumentRequirement(status?.requirements);

    if (!status?.configured || !isTargetAccount || !needsBusinessDocument) {
        return null;
    }

    const deadlineLabel = formatStripeDeadline(status.requirements, collaConfig.fallbackDeadline);
    const deadlineText = deadlineLabel
        ? <> abans del {deadlineLabel}</>
        : <> quan abans millor</>;

    const handleOpenStripe = () => {
        setActionLoading(true);
        setError(null);

        postAPI('/stripe/connect/refresh-onboarding', {}, (data) => {
            setActionLoading(false);
            if (data?.success && data?.onboardingUrl) {
                window.location.href = data.onboardingUrl;
            } else {
                setError(data?.message || 'No s\'ha pogut obrir Stripe. Torna-ho a provar des de Tresoreria > Pagaments.');
            }
        }, () => {
            setActionLoading(false);
            setError('No s\'ha pogut obrir Stripe. Torna-ho a provar des de Tresoreria > Pagaments.');
        });
    };

    return (
        <div className="stripe-verification-banner" role="alert">
            <div className="stripe-verification-banner-content">
                <div className="stripe-verification-banner-copy">
                    <strong>Acció necessària a Stripe:</strong>
                    {' '}
                    cal pujar un document que coincideixi amb les dades fiscals de {collaConfig.displayName}{deadlineText}.
                    {' '}
                    Teniu preparada la targeta NIF o el certificat d&apos;inscripció de l&apos;associació.
                    {error ? (
                        <span className="stripe-verification-banner-error">{error}</span>
                    ) : null}
                </div>
                <div className="stripe-verification-banner-actions">
                    <a className="stripe-verification-banner-link" href="/quotes/stripe">
                        Veure passos
                    </a>
                    <button
                        className="stripe-verification-banner-button"
                        type="button"
                        onClick={handleOpenStripe}
                        disabled={actionLoading}
                    >
                        {actionLoading ? 'Obrint Stripe...' : 'Completar a Stripe'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default StripeVerificationBanner;
